import React from 'react';
import { Box, Skeleton, Typography, Fade, Grow } from '@mui/material';
import { styled } from '@mui/material/styles';

const LoadingContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  zIndex: theme.zIndex.modal + 1,
  backdropFilter: 'blur(4px)',
  transition: 'background-color 0.3s ease-in-out',
}));

const LoadingContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[4],
  transform: 'scale(0.95)',
  opacity: 0,
  transition: 'all 0.3s ease-in-out',
  '&.show': {
    transform: 'scale(1)',
    opacity: 1,
  },
}));

const StyledSkeleton = styled(Skeleton)(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
  '&::after': {
    background: `linear-gradient(90deg, ${theme.palette.grey[200]} 0%, ${theme.palette.grey[300]} 50%, ${theme.palette.grey[200]} 100%)`,
  },
}));

interface LoadingOverlayProps {
  open: boolean;
  message?: string;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ open, message = 'Loading...' }) => {
  const [showContent, setShowContent] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      // Delay showing content slightly after overlay appears
      const timer = setTimeout(() => {
        setShowContent(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
    }
  }, [open]);

  return (
    <Fade in={open} timeout={300}>
      <LoadingContainer>
        <Grow in={showContent} timeout={300}>
          <LoadingContent className={showContent ? 'show' : ''}>
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <StyledSkeleton variant="circular" width={60} height={60} />
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <StyledSkeleton variant="text" width={200} height={24} />
                <StyledSkeleton variant="text" width={150} height={20} />
              </Box>
            </Box>
            <Typography variant="body1" color="text.secondary">
              {message}
            </Typography>
          </LoadingContent>
        </Grow>
      </LoadingContainer>
    </Fade>
  );
};

export default LoadingOverlay; 